import { DBSchema, IDBPDatabase, openDB } from 'idb'
import { OrderPaymentStatus, OrderStatus, ProcessPaymentContract } from 'models'
import { OfflineOrderContract } from 'Services/api/offline/order.service.offline'
import { PendingTransactionEntity } from './models'

const dbName = 'pending-order-db'

interface PendingOrderDB extends DBSchema {
    orders: {
        value: PendingTransactionEntity
        key: string
        indexes: {
            idx_orderStatus: string
        }
    }
}

const getDbConnection = (): Promise<IDBPDatabase<PendingOrderDB>> => {
    return openDB<PendingOrderDB>(dbName, 1, {
        upgrade(db) {
            const orderStore = db.createObjectStore('orders', {
                keyPath: 'orderID',
            })
            orderStore.createIndex('idx_orderStatus', 'orderStatus')
        },
    })
}

const syncableStatus = [OrderStatus.Completed, OrderStatus.Voided]

/** complete transaction is order with status `Complete` or `Voided` */
const isCompletedTransaction = (e: PendingTransactionEntity) =>
    (e.orderStatus === OrderStatus.Completed &&
        e.payments &&
        e.payments.length > 0) ||
    e.orderStatus === OrderStatus.Voided

const pendingOrderDbService = {
    async saveOrder(order: OfflineOrderContract) {
        const db = await getDbConnection()
        if (!order.orderDateTime) order.orderDateTime = new Date().toISOString()

        await db.put('orders', {
            orderID: order.orderID!,
            order: order,
            orderStatus: OrderStatus.Saved,
            orderPaymentStatus: OrderPaymentStatus.Pending,
            payments: null,
            isFailedSync: false,
        })
    },
    async savePayment(payment: ProcessPaymentContract): Promise<boolean> {
        const db = await getDbConnection()
        const tx = db.transaction('orders', 'readwrite')
        const existingOrder = await tx.store.get(payment.orderID!)

        if (!existingOrder) return false

        // Update order in iDB
        existingOrder.payments = !existingOrder.payments
            ? [payment]
            : [...existingOrder.payments, payment]
        const sumPaidValue = existingOrder.payments.reduce(
            (acc, p) =>
                acc +
                (p.payments?.[0]?.tenderedAmount! -
                    p.payments?.[0]?.changeAmount!),
            0,
        )
        const isFullyPaid = sumPaidValue >= existingOrder.order.totalDueAmount!

        existingOrder.orderStatus = isFullyPaid
            ? OrderStatus.Completed
            : OrderStatus.Saved
        existingOrder.orderPaymentStatus = isFullyPaid
            ? OrderPaymentStatus.Paid
            : OrderPaymentStatus.PartiallyPaid

        await tx.store.put(existingOrder)
        await tx.done
        return true
    },
    async update(entity: PendingTransactionEntity) {
        const db = await getDbConnection()
        await db.put('orders', entity)
    },
    async getAll() {
        const db = await getDbConnection()
        return await db.getAll('orders')
    },
    async getById(orderID: string) {
        const db = await getDbConnection()
        return db.get('orders', orderID)
    },
    async deleteById(orderID: string) {
        const db = await getDbConnection()
        db.delete('orders', orderID)
    },
    async hasReadyToSyncOrders() {
        return (await this.getReadyToSyncOrders()).length > 0
    },
    async getReadyToSyncOrders() {
        const db = await getDbConnection()
        const allRecords = await db.getAll('orders')
        return allRecords.filter(
            (e) =>
                syncableStatus.includes(e.orderStatus) &&
                !e.isFailedSync &&
                isCompletedTransaction(e),
        )
    },
}

export default pendingOrderDbService
